import { DialogContent } from '@mui/material';
import FormDialogContainer from '../../FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../DialogActionButtons/DialogActionButtons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { uploadCandidateJobFile } from '../../../Services/CandidateJobs';
import { Dropzone } from '../../Dropzone/Dropzone';
import { uploadCandidateFile } from '../../../Services/Candidates';

interface Props {
  candidateJobId?: number;
  candidateId?: number;
  open: boolean;
  isCandidateFile: boolean;
  handleClose: () => void;
  documentType?: number;
}

export const CandidateJobAttachmentsDialog = ({ candidateJobId, candidateId, open, handleClose, isCandidateFile, documentType }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = t('toast.errors.something_went_wrong');

  const [files, setFiles] = useState<File[]>([]);
  const [loadingFiles, setIsLoadingFiles] = useState(false);

  const handleCloseAndReset = () => {
    setFiles([]);
    setIsLoadingFiles(false);
    handleClose();
  };

  const { mutate: uploadCandidateJobFileMutation } = useMutation({
    mutationFn: (data: FormData) => isCandidateFile 
      ? uploadCandidateFile(`${candidateId}`, data, documentType) 
      : uploadCandidateJobFile(`${candidateJobId}`, data, documentType),
    onError: () => {
      toast.error(errorMessage);
      setIsLoadingFiles(false);
    },
    onSuccess: () => {
      toast.success(t('toast.success.upload'));
      setFiles([]);
      setIsLoadingFiles(false);
      queryClient.invalidateQueries({ queryKey: ['getCandidateFiles'] });
      handleCloseAndReset();
    },
  });

  const handleFileUpload = () => {
    if (files.length === 0) return;

    setIsLoadingFiles(true);
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    uploadCandidateJobFileMutation(formData);
  };

  return (
    <FormDialogContainer open={open} handleClose={handleCloseAndReset}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Dropzone
          onFileUpload={handleFileUpload}
          files={files}
          width="540px"
          setFiles={setFiles}
          loading={loadingFiles}
        />
      </DialogContent>
      <DialogActionButtons
        showConfirmButton={false}
        cancelButtonLabel={t('jobs.close')}
        onCancel={handleCloseAndReset}
      />
    </FormDialogContainer>
  );
};
